import { getExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import type { Store } from '@ms/yammer-data/dist/state/types';
import { ExperimentContextProvider } from '@ms/yammer-experiments-context';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting as useExperimentTreatment,
  useReportExperimentTreatmentCallback,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { PageSectionRefsProvider } from '@ms/yammer-web-components/dist/app/PageSectionRefs';
import { ReduxTranslationsContextProvider } from '@ms/yammer-web-components/dist/translations';
import { AnnouncerProvider } from '@ms/yammer-web-core-ui/dist/components/Announcer';
import GriffelNoopRendererProvider from '@ms/yammer-web-core-ui/dist/components/GriffelNoopRendererProvider';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import type { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

import Main from '../../../components/Main';
import type { YammerWebState } from '../../../state/types';

import RelayAppWrapper from './RelayAppWrapper';
import { getBasename } from './getBasename';
import { getErrorComponent } from './getErrorComponent';

const experimentContext = {
  useReportExperimentTreatmentCallback,
  useIsFeatureRolloutEnabled,
  useExperimentTreatment,
  useAndReportExperimentTreatment,
  getExperimentTreatment,
};

export interface RendererOptions {
  readonly store: Store<YammerWebState>;
  readonly target: HTMLElement;
  readonly persistor?: Persistor;
  readonly isFailure?: boolean;
  readonly etag?: string;
}

export type Renderer = (options: RendererOptions) => void;

const renderer: Renderer = ({ store, target, persistor, isFailure = false, etag }) => {
  const children = isFailure ? getErrorComponent(store) : <Main />;

  const basename = getBasename(store);

  const content = (
    <HelmetProvider>
      <BrowserRouter basename={basename}>
        <CompatRouter>
          <PageSectionRefsProvider>
            <AnnouncerProvider>{children}</AnnouncerProvider>
          </PageSectionRefsProvider>
        </CompatRouter>
      </BrowserRouter>
    </HelmetProvider>
  );

  const contentWithOptionalPersistGate = persistor ? (
    <PersistGate loading={null} persistor={persistor}>
      {content}
    </PersistGate>
  ) : (
    content
  );

  const root = createRoot(target);
  root.render(
    <GriffelNoopRendererProvider>
      <Provider store={store}>
        <ReduxTranslationsContextProvider>
          <ExperimentContextProvider {...experimentContext}>
            <RelayAppWrapper reduxStore={store} etag={etag}>
              {contentWithOptionalPersistGate}
            </RelayAppWrapper>
          </ExperimentContextProvider>
        </ReduxTranslationsContextProvider>
      </Provider>
    </GriffelNoopRendererProvider>
  );
};

export default renderer;
